import {
  getExistingPushSubscription,
  hasEnabledPushNotifications,
  verifyServiceWorkerExistence,
} from './pushHelper';
import store from 'dashboard/store';

const ACTIVE_ACCOUNT_CONTEXT_KEY = 'cw_active_account_context';
const ACTIVE_ACCOUNT_CONTEXT_TTL = 15000;
const ACCOUNT_PATH_REGEX = /\/app\/accounts\/(\d+)(?:\/|$)/i;
const TAB_ID_STORAGE_KEY = 'cw_browser_tab_id';

const currentTabId = (() => {
  if (typeof window === 'undefined') return 'server';

  try {
    const existingId = window.sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (existingId) return existingId;

    const nextId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(TAB_ID_STORAGE_KEY, nextId);
    return nextId;
  } catch (_) {
    return `tab_${Date.now()}`;
  }
})();

const isPageActive = () => {
  if (typeof document === 'undefined') return false;

  const isVisible = document.visibilityState === 'visible';
  const hasFocus =
    typeof document.hasFocus === 'function' ? document.hasFocus() : true;
  return isVisible && hasFocus;
};

const accountIdFromPath = rawPath => {
  const path = rawPath || window?.location?.pathname || '';
  const matched = path.match(ACCOUNT_PATH_REGEX);
  if (!matched) return null;

  const accountId = Number(matched[1]);
  return Number.isFinite(accountId) && accountId > 0 ? accountId : null;
};

const readActiveAccountContext = () => {
  try {
    const rawValue = window.localStorage.getItem(ACTIVE_ACCOUNT_CONTEXT_KEY);
    if (!rawValue) return null;

    return JSON.parse(rawValue);
  } catch (_) {
    return null;
  }
};

const clearActiveAccountContext = () => {
  try {
    const context = readActiveAccountContext();
    if (!context || context.tabId !== currentTabId) return;

    window.localStorage.removeItem(ACTIVE_ACCOUNT_CONTEXT_KEY);
  } catch (_) {
    // Ignore storage failures.
  }
};

const writeActiveAccountContext = accountId => {
  try {
    window.localStorage.setItem(
      ACTIVE_ACCOUNT_CONTEXT_KEY,
      JSON.stringify({
        accountId,
        tabId: currentTabId,
        updatedAt: Date.now(),
      })
    );
  } catch (_) {
    // Ignore storage failures.
  }
};

const syncActiveAccountContext = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const accountId = accountIdFromPath();
  if (!accountId || !isPageActive()) {
    clearActiveAccountContext();
    return;
  }

  writeActiveAccountContext(accountId);
};

const isFreshActiveAccountContext = context => {
  if (!context || !context.updatedAt) return false;
  return Date.now() - Number(context.updatedAt) <= ACTIVE_ACCOUNT_CONTEXT_TTL;
};

const isAnotherFocusedTabOnSameAccount = accountId => {
  const activeContext = readActiveAccountContext();
  if (!activeContext || !isFreshActiveAccountContext(activeContext))
    return false;

  return (
    Number(activeContext.accountId) === Number(accountId) &&
    activeContext.tabId !== currentTabId
  );
};

const installActiveAccountTracking = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__cwActiveAccountTrackingInstalled) return;

  const historyChangeEventName = 'cw:history-change';
  const wrapHistoryMethod = methodName => {
    const originalMethod = window.history[methodName];
    if (typeof originalMethod !== 'function' || originalMethod.__cwWrapped)
      return;

    const wrappedMethod = function wrappedHistoryMethod(...args) {
      const result = originalMethod.apply(this, args);
      window.dispatchEvent(new Event(historyChangeEventName));
      return result;
    };
    wrappedMethod.__cwWrapped = true;
    window.history[methodName] = wrappedMethod;
  };

  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');

  document.addEventListener('visibilitychange', syncActiveAccountContext);
  window.addEventListener('focus', syncActiveAccountContext);
  window.addEventListener('blur', syncActiveAccountContext);
  window.addEventListener('pagehide', clearActiveAccountContext);
  window.addEventListener('beforeunload', clearActiveAccountContext);
  window.addEventListener('popstate', syncActiveAccountContext);
  window.addEventListener(historyChangeEventName, syncActiveAccountContext);

  window.__cwActiveAccountTrackingInstalled = true;
  syncActiveAccountContext();
};

const conversationUrlForNotification = notification => {
  const accountId = Number(notification?.account_id);
  const conversationId = Number(
    notification?.primary_actor?.id || notification?.primary_actor_id
  );

  if (
    Number.isFinite(accountId) &&
    accountId > 0 &&
    Number.isFinite(conversationId) &&
    conversationId > 0
  ) {
    return `/app/accounts/${accountId}/conversations/${conversationId}`;
  }

  return '/app';
};

const notificationTag = notification => {
  const type = notification?.notification_type || 'notification';
  const actorId =
    notification?.primary_actor?.id ||
    notification?.primary_actor_id ||
    'unknown';
  const id = notification?.id || Date.now();
  return `${type}_${actorId}_${id}`;
};

// Resolve the account (brand) name for a notification so the desktop title can
// be prefixed with 【account】 — agents handle multiple brands and need to know
// which one a message belongs to at a glance.
const accountNameForNotification = notification => {
  const accountId = Number(notification?.account_id);
  if (!Number.isFinite(accountId) || accountId <= 0) return '';
  try {
    const account = store.getters['accounts/getAccount'](accountId);
    return String(account?.name || '').trim();
  } catch (_) {
    return '';
  }
};

const notificationTitle = notification => {
  // The title IS the account (brand) name; the body still carries the
  // sender + message. Falls back to the default title if the name is unknown.
  const accountName = accountNameForNotification(notification);
  return accountName || notification?.push_message_title || 'New notification';
};

const notificationPayload = notification => ({
  title: notificationTitle(notification),
  body: notification?.push_message_body || '',
  tag: notificationTag(notification),
  url: conversationUrlForNotification(notification),
  accountId: Number(notification?.account_id) || null,
});

const showWithWindowNotification = payload => {
  const browserNotification = new Notification(payload.title, {
    body: payload.body,
    tag: payload.tag,
    data: {
      url: payload.url,
      accountId: payload.accountId,
    },
  });

  browserNotification.onclick = () => {
    window.focus();
    window.location.href = payload.url;
    browserNotification.close();
  };
};

// Server web push is confirmed working (VAPID keys present, worker logs show
// successful sends). Division of labour for the "tab not active" case:
//   - Browser HAS a push subscription  -> the SERVER push notifies. A push
//     event wakes the service worker even when the tab is throttled or frozen
//     by the browser, so it is the reliable away-path. The client-side
//     notification is skipped to avoid duplicates.
//   - Browser has NO push subscription -> the client-side (WebSocket-driven)
//     notification below is the only channel, so it fires.
// The matching server-side dedup lives in sw.js: server push is skipped only
// when a FOCUSED window on the account exists (the agent is already looking).
const hasRegisteredWebPushSubscription = async () => {
  if (!window?.chatwootConfig?.vapidPublicKey) return false;

  try {
    const subscription = await getExistingPushSubscription();
    return !!subscription;
  } catch (_) {
    return false;
  }
};

installActiveAccountTracking();

export const showRealtimeBrowserNotification = async notification => {
  syncActiveAccountContext();

  if (!hasEnabledPushNotifications()) return false;
  if (isPageActive()) return false;
  if (isAnotherFocusedTabOnSameAccount(notification?.account_id)) return false;
  if (await hasRegisteredWebPushSubscription()) return false;

  const payload = notificationPayload(notification);

  try {
    const registration = await verifyServiceWorkerExistence();
    if (registration?.showNotification) {
      await registration.showNotification(payload.title, {
        body: payload.body,
        tag: payload.tag,
        data: {
          url: payload.url,
          accountId: payload.accountId,
        },
      });
      return true;
    }
  } catch (_) {
    // Fall back to the standard Notification API below.
  }

  try {
    showWithWindowNotification(payload);
    return true;
  } catch (_) {
    return false;
  }
};
